import { Link } from "react-router-dom";
import { TopMenu } from "../../top-menu/TopMenu";
import { Footer } from "../../footer/Footer";
import { PageHero } from "../../page-hero/PageHero";
import TeamWork from '../../../assets/Teamwork_original_537665.jpg';

const VALUES = [
    {
        title: 'Accuracy',
        description: 'Every drawing is reviewed in-house against AISC standards and your shop\'s preferences. Errors cost everyone — we treat them as preventable.',
    },
    {
        title: 'Integrity',
        description: 'We tell you what we can do, what we can\'t, and when. No surprises during fabrication or erection.',
    },
    {
        title: 'Collaboration',
        description: 'We coordinate directly with your design teams and customers, not just hand off files. Your project is our project.',
    },
    {
        title: 'On-Time Delivery',
        description: 'Schedules matter. We commit to dates we can hit and communicate early if anything threatens them.',
    },
];

export const Mission = () => (
    <div className="min-h-screen flex flex-col bg-white">
        <TopMenu />

        <PageHero
            eyebrow="Mission Statement"
            title="Quality drawings."
            accent="Honest partnerships."
            subtitle="We exist to produce shop drawings and 3D models that exceed our customers' expectations — and to be a place our team is proud to work."
        />

        <main className="flex-1">
            <section className="py-20 md:py-28">
                <div className="container-px mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
                    <div className="relative bg-navy-900 rounded-2xl p-10 md:p-12 text-white overflow-hidden">
                        <div className="absolute inset-0 bg-hero-radial opacity-40" />
                        <div className="relative">
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-accent uppercase tracking-wider mb-6">
                                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                                As a Company
                            </div>
                            <h2 className="text-display-md font-display mb-5">
                                High-quality, accurate, on-time.
                            </h2>
                            <p className="text-lg text-steel-200 leading-relaxed">
                                Our mission is to provide high-quality steel shop drawings and models that meet and exceed our
                                customers' expectations. By utilizing the latest technology and adhering to strict
                                <strong className="text-white"> AISC standards</strong>, we ensure every project is accurate,
                                complete, and facilitates efficient coordination for our clients' success.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-steel-100 shadow-card p-10 md:p-12">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy-50 border border-navy-100 text-xs font-semibold text-navy-700 uppercase tracking-wider mb-6 bg-steel-50">
                            <span className="w-1.5 h-1.5 rounded-full bg-navy-700" />
                            As an Individual
                        </div>
                        <h2 className="text-display-md font-display text-navy-900 mb-5">
                            A team built on professional excellence.
                        </h2>
                        <p className="text-lg text-steel-700 leading-relaxed">
                            At Rocky Point Detailing, we are dedicated to building a special place to work — founded on
                            <strong className="text-navy-900"> professional excellence, integrity, and honesty</strong>. We commit to a positive,
                            collaborative environment where every team member contributes to our collective goal: delivering
                            accurate and on-time results for every project.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-steel-50">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="max-w-3xl mb-14">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">What we stand on</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-4">
                            Four values, lived every day.
                        </h2>
                        <p className="text-lg text-steel-600">
                            These aren't posters in the office — they're the standards we hire, work, and review against.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {VALUES.map((value, index) => (
                            <div key={value.title} className="bg-white rounded-2xl p-8 border border-steel-100 shadow-card hover:shadow-card-hover transition-shadow">
                                <div className="flex items-start gap-5">
                                    <span className="font-display text-2xl font-bold text-accent-hover shrink-0 tabular-nums">
                                        0{index + 1}
                                    </span>
                                    <div>
                                        <h3 className="text-xl font-bold font-display text-navy-900 mb-2">{value.title}</h3>
                                        <p className="text-steel-600 leading-relaxed m-0">{value.description}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="container-px mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6">
                        <div className="rounded-2xl overflow-hidden shadow-card border border-steel-100">
                            <img src={TeamWork} alt="Team collaboration" className="w-full h-auto object-cover" />
                        </div>
                    </div>
                    <div className="lg:col-span-6">
                        <p className="text-sm font-semibold text-navy-600 uppercase tracking-[0.2em] mb-4">Why it matters</p>
                        <h2 className="text-display-lg font-display text-navy-900 mb-5">
                            Detailers your shop floor will thank you for.
                        </h2>
                        <p className="text-lg text-steel-700 leading-relaxed mb-6">
                            Bad drawings cost real money — in shop hours, in rework, in delayed erection. Our mission isn't
                            abstract: every choice we make is in service of giving fabricators paper (and machine files) they
                            can build from with confidence.
                        </p>
                        <Link
                            to="/services"
                            className="inline-flex items-center gap-1.5 text-base font-semibold text-navy-700 no-underline hover:text-navy-600 hover:gap-2.5 transition-all"
                        >
                            See what we deliver
                            <Arrow />
                        </Link>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-24">
                <div className="container-px mx-auto max-w-7xl">
                    <div className="bg-navy-900 rounded-2xl p-10 md:p-14 text-center text-white relative overflow-hidden">
                        <div className="absolute inset-0 bg-hero-radial opacity-50" />
                        <div className="relative">
                            <h2 className="text-display-md font-display mb-4">Ready to work with a team that stands behind every drawing?</h2>
                            <p className="text-steel-200 mb-8 max-w-xl mx-auto">
                                Tell us about your project — we'll respond within 48 hours.
                            </p>
                            <Link
                                to="/contact-us"
                                className="inline-flex items-center gap-2 px-7 py-4 rounded-lg bg-signal text-signal-ink font-semibold no-underline hover:bg-signal-hover transition-all shadow-glow-accent hover:scale-[1.02]"
                            >
                                Request a Quote
                                <Arrow />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>

        <Footer />
    </div>
);

const Arrow = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);
